import { useQuery } from '@tanstack/react-query';
import { normalize, schema } from 'normalizr';
import getInsightsInfo from '../../actions/audience/getInsightsInfo';
import sportsApi from '../../utils/sportsApi';

const generateId = (account: any) => `${account.id}`;

const accountSchema = new schema.Entity('accountsById', undefined, {
    idAttribute: generateId,
});

type AudienceInsightsParams = {
    clientId: number;
};

const getAudienceInsights = async ({ clientId }: AudienceInsightsParams) => {
    const query = { clientId };

    const rawAccounts: any[] = await sportsApi
        .url('/audience-insights')
        .query(query)
        .get()
        .json();

    const enrichedAccounts = rawAccounts.map(account => {
        const { platformId, demographicInsights } = account;
        const getInfo = getInsightsInfo(platformId);

        const insights = demographicInsights.reduce(
            (acc, { demographicTypeName, demographicSegments }) => {
                const data = getInfo({
                    demographicTypeName,
                    demographicSegments,
                });

                if (data && Object.keys(data).length) {
                    Object.assign(acc, data);
                }

                return acc;
            },
            {} as Record<string, any>,
        );

        return {
            ...account,
            insights,
            isAuthenticated: demographicInsights.length > 0,
        };
    });

    const normalized = normalize(enrichedAccounts, [accountSchema]);
    return {
        order: normalized.result,
        accountsById: normalized.entities.accountsById || {},
    };
};

export const useAudienceInsights = (params: AudienceInsightsParams) =>
    useQuery({
        queryKey: ['audience-insights', params],
        queryFn: () => getAudienceInsights(params),
        staleTime: 600000,
        enabled: !!params.clientId,
    });
